import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPut } from '../../api/client';
import MediaPicker from '../../components/admin/MediaPicker';
import OptionGroup from './pageEditor/OptionGroup';
import { DragHandle, SortableItem, reorder } from './pageEditor/sortable';

const emptyAbout = () => ({
  title: 'About ZeMaxi',
  seo: { description: '', keywords: '', url: '' },
  content: {
    heroSubtitle: '',
    profileImage: '',
    storyHeading: 'My Story',
    storyParagraphs: [],
    skillsHeading: 'Technical Skills',
    skillsIntro: '',
    skills: [],
  },
});

const emptyStream = () => ({
  title: 'Live Stream',
  seo: { description: '', keywords: '', url: '' },
  content: {
    heroSubtitle: '',
    offlineHeading: 'Currently Offline',
    offlineText: '',
    offlineLinks: [],
    platformHeading: 'Watch Live',
  },
});

function SeoFields({ page, onChange }) {
  const seo = page.seo || {};
  const setSeo = (patch) => onChange({ ...page, seo: { ...seo, ...patch } });

  return (
    <OptionGroup title="SEO" defaultOpen={false}>
      <label>
        Page title
        <input value={page.title || ''} onChange={(event) => onChange({ ...page, title: event.target.value })} />
      </label>
      <label>
        Description
        <textarea rows={3} value={seo.description || ''} onChange={(event) => setSeo({ description: event.target.value })} />
      </label>
      <label>
        Keywords
        <input value={seo.keywords || ''} onChange={(event) => setSeo({ keywords: event.target.value })} />
      </label>
      <label>
        Canonical URL
        <input value={seo.url || ''} onChange={(event) => setSeo({ url: event.target.value })} />
      </label>
    </OptionGroup>
  );
}

function AboutForm({ page, onChange }) {
  const content = page.content || {};
  const setContent = (patch) => onChange({ ...page, content: { ...content, ...patch } });
  const paragraphs = content.storyParagraphs || [];
  const skills = content.skills || [];

  return (
    <div className="admin-form">
      <OptionGroup title="Hero">
        <label>
          Subtitle
          <textarea rows={2} value={content.heroSubtitle || ''} onChange={(event) => setContent({ heroSubtitle: event.target.value })} />
        </label>
        <MediaPicker label="Profile image" value={content.profileImage || ''} onChange={(url) => setContent({ profileImage: url })} />
      </OptionGroup>
      <OptionGroup title="Story">
        <label>
          Story heading
          <input value={content.storyHeading || ''} onChange={(event) => setContent({ storyHeading: event.target.value })} />
        </label>
        {paragraphs.length === 0 && <p className="tpb-sidebar-hint">No story paragraphs yet.</p>}
        {paragraphs.map((paragraph, index) => (
          <SortableItem
            key={index}
            listId="about-paragraphs"
            index={index}
            className="tpb-repeat-block"
            onMove={(from, to) => setContent({ storyParagraphs: reorder(paragraphs, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="about-paragraphs" index={index} />
              <strong>Paragraph {index + 1}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove paragraph" onClick={() => setContent({ storyParagraphs: paragraphs.filter((_, i) => i !== index) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <textarea
              rows={4}
              value={paragraph}
              onChange={(event) => {
                const storyParagraphs = [...paragraphs];
                storyParagraphs[index] = event.target.value;
                setContent({ storyParagraphs });
              }}
            />
          </SortableItem>
        ))}
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setContent({ storyParagraphs: [...paragraphs, ''] })}>
          <i className="fas fa-plus" aria-hidden="true" /> Add paragraph
        </button>
      </OptionGroup>
      <OptionGroup title="Skills">
        <label>
          Skills heading
          <input value={content.skillsHeading || ''} onChange={(event) => setContent({ skillsHeading: event.target.value })} />
        </label>
        <label>
          Intro
          <textarea rows={2} value={content.skillsIntro || ''} onChange={(event) => setContent({ skillsIntro: event.target.value })} />
        </label>
        {skills.length === 0 && <p className="tpb-sidebar-hint">No skills yet.</p>}
        {skills.map((skill, index) => (
          <SortableItem
            key={index}
            listId="about-skills"
            index={index}
            className="tpb-repeat-block"
            onMove={(from, to) => setContent({ skills: reorder(skills, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="about-skills" index={index} />
              <strong>{skill.label || `Skill ${index + 1}`}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove skill" onClick={() => setContent({ skills: skills.filter((_, i) => i !== index) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <div className="admin-grid-2">
              <label>
                Label
                <input value={skill.label || ''} onChange={(event) => {
                  const next = [...skills];
                  next[index] = { ...skill, label: event.target.value };
                  setContent({ skills: next });
                }} />
              </label>
              <label>
                Icon class
                <input value={skill.icon || ''} placeholder="fas fa-code" onChange={(event) => {
                  const next = [...skills];
                  next[index] = { ...skill, icon: event.target.value };
                  setContent({ skills: next });
                }} />
              </label>
            </div>
          </SortableItem>
        ))}
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setContent({ skills: [...skills, { icon: 'fas fa-star', label: '' }] })}>
          <i className="fas fa-plus" aria-hidden="true" /> Add skill
        </button>
      </OptionGroup>
      <SeoFields page={page} onChange={onChange} />
    </div>
  );
}

function StreamLandingForm({ page, onChange }) {
  const content = page.content || {};
  const setContent = (patch) => onChange({ ...page, content: { ...content, ...patch } });
  const links = content.offlineLinks || [];

  return (
    <div className="admin-form">
      <OptionGroup title="Hero">
        <label>
          Subtitle
          <textarea rows={2} value={content.heroSubtitle || ''} onChange={(event) => setContent({ heroSubtitle: event.target.value })} />
        </label>
        <label>
          Platform heading
          <input value={content.platformHeading || ''} onChange={(event) => setContent({ platformHeading: event.target.value })} />
        </label>
      </OptionGroup>
      <OptionGroup title="Offline message">
        <label>
          Heading
          <input value={content.offlineHeading || ''} onChange={(event) => setContent({ offlineHeading: event.target.value })} />
        </label>
        <label>
          Text
          <textarea rows={3} value={content.offlineText || ''} onChange={(event) => setContent({ offlineText: event.target.value })} />
        </label>
        {links.length === 0 && <p className="tpb-sidebar-hint">No offline links yet.</p>}
        {links.map((link, index) => (
          <SortableItem
            key={index}
            listId="offline-links"
            index={index}
            className="tpb-repeat-block"
            onMove={(from, to) => setContent({ offlineLinks: reorder(links, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="offline-links" index={index} />
              <strong>{link.label || `Link ${index + 1}`}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove link" onClick={() => setContent({ offlineLinks: links.filter((_, i) => i !== index) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <div className="admin-grid-2">
              <label>
                Label
                <input value={link.label || ''} onChange={(event) => {
                  const offlineLinks = [...links];
                  offlineLinks[index] = { ...link, label: event.target.value };
                  setContent({ offlineLinks });
                }} />
              </label>
              <label>
                Path
                <input value={link.href || ''} onChange={(event) => {
                  const offlineLinks = [...links];
                  offlineLinks[index] = { ...link, href: event.target.value };
                  setContent({ offlineLinks });
                }} />
              </label>
            </div>
          </SortableItem>
        ))}
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={() => setContent({ offlineLinks: [...links, { href: '/', label: '' }] })}>
          <i className="fas fa-plus" aria-hidden="true" /> Add link
        </button>
      </OptionGroup>
      <SeoFields page={page} onChange={onChange} />
    </div>
  );
}

export default function AdminPages() {
  const [activeTab, setActiveTab] = useState('about');
  const [editMode, setEditMode] = useState('form');
  const [about, setAbout] = useState(emptyAbout());
  const [stream, setStream] = useState(emptyStream());
  const [jsonText, setJsonText] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    Promise.all([apiGet('/pages/about'), apiGet('/pages/stream')])
      .then(([aboutPage, streamPage]) => {
        setAbout({ ...emptyAbout(), ...aboutPage, content: { ...emptyAbout().content, ...(aboutPage.content || {}) } });
        setStream({ ...emptyStream(), ...streamPage, content: { ...emptyStream().content, ...(streamPage.content || {}) } });
      })
      .catch(console.error);
  }, []);

  const page = activeTab === 'about' ? about : stream;
  const setPage = activeTab === 'about' ? setAbout : setStream;

  const showJson = () => {
    setJsonText(JSON.stringify(page, null, 2));
    setEditMode('json');
    setMessage('');
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setPage(parsed);
      setEditMode('form');
      setMessage('JSON applied. Save to keep it.');
    } catch (err) {
      setMessage(`Invalid JSON: ${err.message}`);
    }
  };

  const saveActive = async () => {
    setMessage('');
    try {
      const current = editMode === 'json' ? JSON.parse(jsonText) : page;
      if (editMode === 'json') setPage(current);
      await apiPut(`/pages/${activeTab}`, {
        title: current.title,
        seo: current.seo,
        content: current.content,
      });
      setMessage(activeTab === 'about' ? 'About page saved.' : 'Stream landing page saved.');
    } catch (err) {
      setMessage(err.message);
    }
  };

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-panel-tabs">
            <button type="button" className={`admin-panel-tab ${activeTab === 'about' ? 'active' : ''}`} onClick={() => { setActiveTab('about'); setEditMode('form'); setMessage(''); }}>
              About
            </button>
            <button type="button" className={`admin-panel-tab ${activeTab === 'stream' ? 'active' : ''}`} onClick={() => { setActiveTab('stream'); setEditMode('form'); setMessage(''); }}>
              Stream Landing
            </button>
          </div>
          <div className="admin-panel-toolbar-actions">
            <button type="button" className={`btn btn-secondary ${editMode === 'form' ? 'is-active' : ''}`} onClick={() => setEditMode('form')}>
              Form
            </button>
            <button type="button" className="btn btn-secondary" onClick={showJson}>JSON</button>
            <a href={activeTab === 'about' ? '/about' : '/stream'} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              <i className="fas fa-external-link-alt" aria-hidden="true" /> Preview
            </a>
            <button type="button" className="btn btn-primary" onClick={saveActive}>
              Save {activeTab === 'about' ? 'About' : 'Stream Landing'}
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'stream' && (
        <div className="admin-notice-banner">
          <p>
            Schedule, Stream Info, Setup, and custom pages are edited in{' '}
            <Link to="/admin/stream-pages">Stream Pages</Link>.
            Game calendar entries are managed in <Link to="/admin/schedule">Schedule Games</Link>.
          </p>
        </div>
      )}

      {message && <p className="admin-message">{message}</p>}

      {editMode === 'json' ? (
        <div className="admin-json-panel">
          <div className="admin-json-panel-head">
            <h3>JSON</h3>
            <button type="button" className="btn btn-primary" onClick={applyJson}>Apply JSON</button>
          </div>
          <textarea className="admin-json" value={jsonText} onChange={(event) => setJsonText(event.target.value)} rows={24} spellCheck={false} />
        </div>
      ) : (
        <div className="admin-json-panel">
          {activeTab === 'about'
            ? <AboutForm page={about} onChange={setAbout} />
            : <StreamLandingForm page={stream} onChange={setStream} />}
        </div>
      )}
    </div>
  );
}
